import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Building2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const STAR_RATINGS = [
  { value: '3', label: '3 Star' },
  { value: '4', label: '4 Star' },
  { value: '5', label: '5 Star' },
  { value: '5D', label: '5 Star Deluxe' },
];

const HOTEL_TYPES = [
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Resort', label: 'Resort' },
  { value: 'Homestay', label: 'Homestay' },
  { value: 'Villa', label: 'Villa' },
  { value: 'Boutique', label: 'Boutique' },
];

const QuickAddHotelModal = ({ 
  open, 
  onOpenChange, 
  prefillName = '',
  prefillPlaceId = '',
  places = [],
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    hotel_name: prefillName,
    place_id: prefillPlaceId,
    star_rating: '4',
    hotel_type: 'Hotel',
    room_name: '',
  });

  // Reset form when modal opens with new prefill
  useEffect(() => {
    if (open) {
      setForm(prev => ({
        ...prev,
        hotel_name: prefillName,
        place_id: prefillPlaceId || prev.place_id,
      }));
    }
  }, [open, prefillName, prefillPlaceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.hotel_name.trim()) {
      toast.error('Hotel name is required');
      return;
    }
    
    if (!form.place_id) {
      toast.error('Please select a location');
      return;
    }

    try {
      setLoading(true);
      
      // Create hotel
      const response = await api.post('/hotels', {
        hotel_name: form.hotel_name.trim(),
        place_id: form.place_id,
        star_rating: form.star_rating,
        hotel_type: form.hotel_type,
        is_active: true,
      });

      const newHotel = response.data;

      // If room name provided, create a basic room configuration
      if (form.room_name.trim()) {
        try {
          await api.post('/hotel-rates/room-configs', {
            hotel_id: newHotel.hotel_id,
            room_name: form.room_name.trim(),
            room_type: 'Standard',
            room_category: 'Standard',
            max_occupancy: 3,
            is_active: true,
          });
        } catch (roomErr) {
          console.warn('Could not create room config:', roomErr);
          // Don't fail the whole operation if room creation fails
        }
      }

      toast.success(`Hotel "${form.hotel_name}" added successfully`);
      
      // Call success callback with the new hotel data
      if (onSuccess) {
        onSuccess(newHotel);
      }
      
      // Reset form and close
      setForm({
        hotel_name: '',
        place_id: '',
        star_rating: '4',
        hotel_type: 'Hotel',
        room_name: '',
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create hotel:', error);
      const message = error.response?.data?.detail || 'Failed to add hotel';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlace = places.find(p => p.place_id === form.place_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-add-hotel-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Quick Add Hotel
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hotel_name">Hotel Name <span className="text-red-500">*</span></Label>
            <Input
              id="hotel_name"
              value={form.hotel_name}
              onChange={(e) => setForm({ ...form, hotel_name: e.target.value })}
              placeholder="e.g., Mountain View Resort"
              data-testid="quick-add-hotel-name"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="place">Location <span className="text-red-500">*</span></Label>
            <Select value={form.place_id} onValueChange={(v) => setForm({ ...form, place_id: v })}>
              <SelectTrigger data-testid="quick-add-hotel-place">
                <SelectValue placeholder="Select location">
                  {selectedPlace?.place_name || 'Select location'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {places.filter(p => p.place_id).map(place => (
                  <SelectItem key={place.place_id} value={place.place_id}>
                    {place.place_name} {place.state ? `(${place.state})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Star Rating</Label>
              <Select value={form.star_rating} onValueChange={(v) => setForm({ ...form, star_rating: v })}>
                <SelectTrigger data-testid="quick-add-hotel-star">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAR_RATINGS.map(star => (
                    <SelectItem key={star.value} value={star.value}>{star.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Hotel Type</Label>
              <Select value={form.hotel_type} onValueChange={(v) => setForm({ ...form, hotel_type: v })}>
                <SelectTrigger data-testid="quick-add-hotel-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOTEL_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="room_name">Room Name (optional)</Label>
            <Input
              id="room_name"
              value={form.room_name}
              onChange={(e) => setForm({ ...form, room_name: e.target.value })}
              placeholder="e.g., Standard A/c Room, Premium Cottage"
              data-testid="quick-add-hotel-room"
            />
            <p className="text-xs text-gray-500">
              Creates a basic room configuration. Add full details later in Hotels Master.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="quick-add-hotel-submit">
              <Plus className="w-4 h-4 mr-1" />
              {loading ? 'Adding...' : 'Add & Select'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddHotelModal;
